/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

/**
 * Reduced-motion feature detection.
 *
 * Callers get `true` when EITHER the OS-level
 * `prefers-reduced-motion: reduce` media query matches, OR the user has
 * flipped the in-app "Reduce motion" toggle on the Settings page
 * (stored under {@link REDUCE_MOTION_KEY} in localStorage). Guarded
 * against non-browser hosts (SSR, jest-node, older WebViews) — every
 * fall-through returns `false` so callers that gate motion on
 * `!prefersReducedMotion()` keep animating by default. That's the safer
 * bias: an accidental `true` would silently disable animations for
 * everyone.
 *
 * Use this from JavaScript to gate programmatic smooth-scroll and other
 * script-driven transitions. Pure-CSS transitions should continue to use
 * an `@media (prefers-reduced-motion: reduce)` SCSS block for the
 * OS-level signal; if a call site needs to react to the in-app toggle at
 * runtime, subscribe to {@link reduceMotion$}.
 */

import { data, type ObservableValue } from '@amazon/vinyl-observable'
import { globalData } from '@amzn/amp-playback'

/** localStorage key for the in-app "Reduce motion" toggle. */
export const REDUCE_MOTION_KEY = 'reduceMotion'

function readMediaQuery(): boolean {
    if (typeof window === 'undefined') return false
    const mm = (window as { matchMedia?: (q: string) => { matches: boolean } }).matchMedia
    if (typeof mm !== 'function') return false
    try {
        return mm.call(window, '(prefers-reduced-motion: reduce)').matches
    } catch {
        return false
    }
}

const defaultOn$ = globalData(() => data(false))

// Synchronous on purpose: callers gate transitions inline, before an async read could settle.
// An explicit stored choice, either way, beats the performance tier's default.
function readToggle(): boolean {
    try {
        const stored = localStorage.getItem(REDUCE_MOTION_KEY)
        return stored === null ? defaultOn$.value : stored === 'true'
    } catch {
        return defaultOn$.value
    }
}

// Module-scope observable — seeded from OR of media query + toggle so
// first read is correct even before any subscription. Kept private and
// re-exposed as an `ObservableValue` so external callers can only read
// / subscribe (mutation goes through `writeReduceMotionToggle`).
const reduceMotionState$ = globalData(() => data(readMediaQuery() || readToggle()))

// Live-follow the OS media query so a system-level flip refreshes
// subscribers without a page reload. Guarded so SSR / non-browser hosts
// don't throw at module load.
if (typeof window !== 'undefined') {
    const mm = (window as { matchMedia?: (q: string) => MediaQueryList }).matchMedia
    if (typeof mm === 'function') {
        try {
            const mql = mm.call(window, '(prefers-reduced-motion: reduce)')
            mql.addEventListener('change', () => {
                reduceMotionState$.value = readMediaQuery() || readToggle()
            })
        } catch {
            /* ignore — older WebViews may throw on unknown queries */
        }
    }
}

/**
 * Sets what the in-app toggle reads as until the user chooses; an explicit choice, either way,
 * always wins.
 */
export function setReduceMotionDefault(next: boolean): void {
    defaultOn$.value = next
    reduceMotionState$.value = readMediaQuery() || readToggle()
}

/**
 * Observable form of {@link prefersReducedMotion} — subscribe from a
 * long-lived callsite (e.g. a card that wants to swap an animated
 * background for a static one at runtime when the user flips the
 * toggle).
 */
export const reduceMotion$: ObservableValue<boolean> = reduceMotionState$

/**
 * Snapshot: `true` if EITHER the OS-level `prefers-reduced-motion:
 * reduce` media query matches, OR the in-app toggle is on.
 */
export function prefersReducedMotion(): boolean {
    return readMediaQuery() || readToggle()
}

/**
 * Read the in-app toggle only (ignores the OS media query). Used by the
 * Settings page to render the toggle's current state without coupling it
 * to the system pref.
 */
export function readReduceMotionToggle(): boolean {
    return readToggle()
}

/**
 * Persist the in-app toggle and update {@link reduceMotion$} so
 * subscribers re-render immediately.
 */
export function writeReduceMotionToggle(next: boolean): void {
    try {
        localStorage.setItem(REDUCE_MOTION_KEY, String(next))
    } catch {
        /* private-mode Safari — ignore. */
    }
    reduceMotionState$.value = readMediaQuery() || next
}

/**
 * Convenience: pick between `'smooth'` and `'auto'` for a `ScrollToOptions`
 * / `ScrollIntoViewOptions` `behavior`. Callers pass this straight into
 * `scrollTo({ behavior: smoothScrollBehavior() })` — under reduced motion
 * the scroll snaps instantly, matching the user's system-level preference.
 */
export function smoothScrollBehavior(): ScrollBehavior {
    return prefersReducedMotion() ? 'auto' : 'smooth'
}
